import React, { useRef, useState } from "react";
import {
  Box,
  Dialog,
  IconButton,
  Popover,
  Slide,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import AddIcon from "@mui/icons-material/Add";
import PhotoIcon from "@mui/icons-material/Photo";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import DescriptionIcon from "@mui/icons-material/Description";
import VideocamIcon from "@mui/icons-material/Videocam";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { lightTheme } from "../../styles/theme";
import { NOTICE_VIDEO_INPUT_ACCEPT } from "../../constants/uploadLimits";
import DriveImagePicker from "../Drive/DriveImagePicker";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export interface NoticeComposerAttachmentPickerProps {
  disabled?: boolean;
  handleFileUpload?: (files: FileList) => Promise<void>;
  handleAddAttachmentFromUrl?: (
    url: string,
    fileName?: string,
    contentType?: string,
  ) => void;
}

/**
 * Plus-button attachment picker (photos, library, camera, document, video).
 * Same options as TitleStep; used on ContentStep for single-user DMs.
 */
const NoticeComposerAttachmentPicker: React.FC<
  NoticeComposerAttachmentPickerProps
> = ({ disabled, handleFileUpload, handleAddAttachmentFromUrl }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isDriveOpen, setIsDriveOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const attachmentOptions = [
    { id: "drive", label: "Drive", icon: FolderOutlinedIcon, color: theme.palette.primary.main, disabled: !handleAddAttachmentFromUrl },
    {
      id: "photos",
      label: "Photos",
      icon: PhotoIcon,
      color: theme.palette.custom.navNoticeFeed,
    },
    {
      id: "camera",
      label: "Camera",
      icon: CameraAltIcon,
      color: lightTheme.colors.textStrong,
    },
    {
      id: "document",
      label: "Document",
      icon: DescriptionIcon,
      color: lightTheme.colors.materialBlue,
    },
    {
      id: "video",
      label: "Video",
      icon: VideocamIcon,
      color: lightTheme.colors.pinkStrong,
    },
  ];

  const uploadAndReset = async (
    input: HTMLInputElement | null,
    files: FileList | null,
  ) => {
    if (!files?.length || !handleFileUpload) return;
    await handleFileUpload(files);
    if (input) input.value = "";
    setShowAttachmentMenu(false);
    setAnchorEl(null);
  };

  const handleAttachmentOption = (option: string) => {
    setShowAttachmentMenu(false);
    setAnchorEl(null);
    switch (option) {
      case "photos":
        fileInputRef.current?.click();
        break;
      case "camera":
        cameraInputRef.current?.click();
        break;
      case "document":
        documentInputRef.current?.click();
        break;
      case "video":
        videoInputRef.current?.click();
        break;
      case "drive":
        setIsDriveOpen(true);
        break;
    }
  };


  const handleDriveSelect = (
    url: string,
    fileName: string,
    contentType?: string,
  ) => {
    handleAddAttachmentFromUrl?.(url, fileName, contentType);
    setIsDriveOpen(false);
  };

  return (
    <>
      <input
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        ref={fileInputRef}
        onChange={(e) => uploadAndReset(e.currentTarget, e.target.files)}
      />
      <input
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        ref={cameraInputRef}
        onChange={(e) => uploadAndReset(e.currentTarget, e.target.files)}
      />
      <input
        type="file"
        accept=".pdf,.doc,.docx,.txt,.rtf,.odt"
        style={{ display: "none" }}
        ref={documentInputRef}
        onChange={(e) => uploadAndReset(e.currentTarget, e.target.files)}
      />
      <input
        type="file"
        accept={NOTICE_VIDEO_INPUT_ACCEPT}
        style={{ display: "none" }}
        ref={videoInputRef}
        onChange={(e) => uploadAndReset(e.currentTarget, e.target.files)}
      />
      <IconButton
        disabled={disabled}
        onClick={(e) => {
          if (disabled) return;
          if (isMobile) {
            setShowAttachmentMenu(true);
          } else {
            setAnchorEl(e.currentTarget);
          }
        }}
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: "common.white",
          width: { xs: 32, sm: 36 },
          height: { xs: 32, sm: 36 },
          borderRadius: "50%",
          border: `2px solid ${theme.palette.primary.main}`,
          "&:hover": {
            backgroundColor: theme.palette.primary.dark,
            borderColor: theme.palette.primary.dark,
            transform: "scale(1.05)",
          },
          transition: "all 0.2s ease",
          flexShrink: 0,
          "&:disabled": {
            backgroundColor: "grey.300",
            borderColor: "grey.300",
            color: "grey.500",
            opacity: 0.75,
            cursor: "not-allowed",
          },
        }}
        aria-label="Add attachment"
      >
        <AddIcon />
      </IconButton>

      {isMobile && (
        <Dialog
          open={showAttachmentMenu}
          onClose={() => setShowAttachmentMenu(false)}
          TransitionComponent={Transition}
          fullScreen
          PaperProps={{
            sx: {
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              m: 0,
              maxHeight: "60vh",
              borderRadius: "24px 24px 0 0",
              backgroundColor: lightTheme.colors.grey100,
            },
          }}
          sx={{
            "& .MuiBackdrop-root": {
              backgroundColor: lightTheme.colors.blackOverlay50,
            },
          }}
        >
          <Box sx={{ p: 3, pt: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 4,
                backgroundColor: lightTheme.colors.materialGrey400,
                borderRadius: 2,
                mx: "auto",
                mb: 3,
              }}
            />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 3,
              }}
            >
              {attachmentOptions.map((option) => {
                const IconComponent = option.icon;
                return (
                  <Box
                    key={option.id}
                    onClick={() => handleAttachmentOption(option.id)}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                      cursor: "pointer",
                      "&:active": { transform: "scale(0.95)" },
                      transition: "transform 0.1s ease",
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor: "common.white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: `0 2px 8px ${lightTheme.colors.shadowDark}`,
                      }}
                    >
                      <IconComponent
                        sx={{ fontSize: 32, color: option.color }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.75rem",
                        color: lightTheme.colors.textStrong,
                        textAlign: "center",
                        fontWeight: 500,
                      }}
                    >
                      {option.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Dialog>
      )}

      {!isMobile && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              minWidth: 240,
              maxWidth: 240,
              borderRadius: 2,
              backgroundColor: "common.white",
              boxShadow: `0 4px 20px ${lightTheme.colors.shadowBlack15}`,
              overflow: "hidden",
              py: 0.5,
              mt: 1,
            },
          }}
        >
          <Box>
            {attachmentOptions.map((option) => {
              const IconComponent = option.icon;
              return (
                <Box
                  key={option.id}
                  onClick={() => handleAttachmentOption(option.id)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    px: 3,
                    py: 1.5,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: lightTheme.colors.grey100,
                    },
                    transition: "background-color 0.2s ease",
                  }}
                >
                  <IconComponent
                    sx={{ fontSize: 24, color: option.color }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.9375rem",
                      color: lightTheme.colors.grey900,
                      fontWeight: 400,
                    }}
                  >
                    {option.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Popover>
      )}

      {isDriveOpen && <DriveImagePicker onClose={() => setIsDriveOpen(false)} onSelect={file => handleDriveSelect(file.url, file.name.split("/").pop() || "image", file.type)} />}
    </>
  );
};

export default NoticeComposerAttachmentPicker;
